import { apiClient } from './apiClient';

const BASE_URL = '/admin/prompt-refinement';

export interface ErrorTypeCount {
  missing: number;
  format_incorrect: number;
  field_confusion: number;
  hallucination: number;
  value_mismatch: number;
}

export interface FieldErrorExample {
  document_filename: string;
  expected: string | null;
  extracted: string | null;
  error_type: string;
}

export interface FieldAnalysis {
  field_name: string;
  error_rate: number;
  error_count: number;
  dominant_error_type: string;
  error_types: ErrorTypeCount;
  examples: FieldErrorExample[];
}

export interface RunErrorAnalysis {
  run_id: string;
  prompt_id: string | null;
  prompt_version: string | null;
  total_documents: number;
  total_fields_evaluated: number;
  overall_accuracy: number;
  fields: FieldAnalysis[];
}

export interface RefinementSession {
  id: string;
  benchmark_run_id: string;
  source_prompt_id: string;
  candidate_prompt_id: string | null;
  candidate_schema_id: string | null;
  status: 'pending' | 'generated' | 'accepted' | 'applied';
  note: string | null;
  tags: string[];
  error_analysis: Record<string, unknown> | null;
  suggestions: Record<string, unknown> | null;
  accepted_suggestions: Record<string, unknown> | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

export interface CandidatePromptResponse {
  id: string;
  version: string;
  name: string;
  parent_prompt_id: string | null;
  is_active: boolean;
  domain: string;
  description: string | null;
}

export interface FieldModification {
  field_name: string;
  sub_field: string;
  current_value: string | null;
  proposed_value: string;
}

export interface CandidateSchemaResponse {
  id: string;
  name: string;
  version: string;
  parent_schema_id: string | null;
  parent_schema_name: string | null;
  parent_schema_version: string | null;
  fields_modified: FieldModification[];
  created_at: string;
}

export interface RefinementSessionEnriched extends RefinementSession {
  run_note: string | null;
  run_domain: string | null;
  source_prompt_name: string | null;
  source_prompt_version: string | null;
  source_schema_name: string | null;
  source_schema_version: string | null;
  candidate_prompt_name: string | null;
  candidate_prompt_version: string | null;
  candidate_schema_name: string | null;
  candidate_schema_version: string | null;
}

export interface PreviewRequest {
  run_id: string;
  selected_fields?: string[];
  llm_config_id?: string;
  suggestion_targets?: string[];
  error_rate_threshold?: number;
}

export interface PreviewResponse {
  system_prompt: string;
  user_prompt: string;
  model_name: string;
  model_display_name: string;
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  estimated_cost: number;
  estimated_time_seconds: number;
  fields_count: number;
  field_names: string[];
}

export interface CandidateListItem {
  id: string;
  name: string;
  version: string;
  domain: string;
  is_active: boolean;
  created_at: string | null;
}

export interface SessionCandidates {
  schemas: CandidateListItem[];
  prompts: CandidateListItem[];
}

export interface GenerateParams {
  run_id: string;
  llm_config_id?: string;
  selected_fields?: string[];
  suggestion_targets?: string[];
  error_rate_threshold?: number;
  max_retries?: number;
}

export const promptRefinementService = {
  async getErrorAnalysisSummary(runId: string): Promise<RunErrorAnalysis> {
    const response = await apiClient.get<RunErrorAnalysis>(
      `/benchmark/runs/${runId}/error-analysis/summary`
    );
    return response.data;
  },

  async preview(params: PreviewRequest): Promise<PreviewResponse> {
    const response = await apiClient.post<PreviewResponse>(
      `${BASE_URL}/preview`,
      params
    );
    return response.data;
  },

  async generate(params: GenerateParams): Promise<RefinementSession> {
    const response = await apiClient.post<RefinementSession>(
      `${BASE_URL}/generate`,
      params
    );
    return response.data;
  },

  async listSessions(domain?: string, status?: string): Promise<RefinementSessionEnriched[]> {
    const params = new URLSearchParams();
    if (domain) params.append('domain', domain);
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params}` : '';
    const response = await apiClient.get<RefinementSessionEnriched[]>(
      `${BASE_URL}/sessions${query}`
    );
    return response.data;
  },

  async listSessionCandidates(sessionId: string): Promise<SessionCandidates> {
    const response = await apiClient.get<SessionCandidates>(
      `${BASE_URL}/sessions/${sessionId}/candidates`
    );
    return response.data;
  },

  async getSession(sessionId: string): Promise<RefinementSession> {
    const response = await apiClient.get<RefinementSession>(
      `${BASE_URL}/sessions/${sessionId}`
    );
    return response.data;
  },

  async patchMetadata(
    sessionId: string,
    data: { note?: string; tags?: string[] }
  ): Promise<RefinementSession> {
    const response = await apiClient.patch<RefinementSession>(
      `${BASE_URL}/sessions/${sessionId}/metadata`,
      data
    );
    return response.data;
  },

  async patchSuggestions(
    sessionId: string,
    acceptedSuggestions: Record<string, unknown>
  ): Promise<RefinementSession> {
    const response = await apiClient.patch<RefinementSession>(
      `${BASE_URL}/sessions/${sessionId}/suggestions`,
      { accepted_suggestions: acceptedSuggestions }
    );
    return response.data;
  },

  async apply(sessionId: string): Promise<CandidatePromptResponse> {
    const response = await apiClient.post<CandidatePromptResponse>(
      `${BASE_URL}/sessions/${sessionId}/apply`
    );
    return response.data;
  },

  async applySchema(sessionId: string): Promise<CandidateSchemaResponse> {
    const response = await apiClient.post<CandidateSchemaResponse>(
      `${BASE_URL}/sessions/${sessionId}/apply-schema`
    );
    return response.data;
  },
};
